// =============================================================================
// RevivedLeadsPanel — "Revived Lost Leads" card on the admin dashboard
// =============================================================================
// 2026-10-01: now reads get_lost_leads_analytics (via useLostLeadsAnalytics), the
// SAME SQL function as the Lost Leads "Analytics" tab. Mako: "i have this revived
// lost leads number right here but i can't genuinely tell what they're from if this
// is accurate who closed what and where". Two rules used to exist:
//   - this card: claim, then a sales call within 30 days (no dial needed, closer not
//     checked) — a client-side slice in utils/revivedMetrics.ts, now retired here;
//   - the Booked tab: the /lostlead rule.
// Mako ruled the /lostlead rule wins. Rule C (2026-10-01, SSOT get_claim_revivals): a
// claim is revived when a call was booked while the claim was LIVE and the claimer
// closed or set the sale; its cash is those credited sales only. All-time Rayston
// moved $168,350 -> $159,851 (old /lostlead) -> $95,350 (rule C).
//
// Range = CLAIM date (Mako ruling 2026-10-01), not call date: "which claims made in
// this range paid off". Every number links back to a row in the Analytics tab's
// "Revived deals" ledger (claimer, closer, cash) — the "who closed what" answer.
// Close rates: final (scored, R30) AND pending (shown, R29), same as every surface.
// =============================================================================

import React from "react";
import { Link } from "react-router-dom";
import MetricCard from "./MetricCard";
import { formatUSD, formatNumber, pctOrDash } from "../../utils/formatters";
import {
  showRateDenominator,
  creditedCloseCounts,
  type LostLeadsGroupMetrics,
} from "../../hooks/data-fetching/useLostLeadsAnalytics";

interface RevivedLeadsPanelProps {
  /** revived_totals from get_lost_leads_analytics (null = no revived claims in range). */
  revived: LostLeadsGroupMetrics | null;
  /** All claims in range, for the empty state ("N claims, none revived"). */
  claimsInRange: number;
  /** Range label echoed from the dashboard header, e.g. "Last 30 Days". */
  rangeLabel?: string | null;
  loading?: boolean;
  error?: string | null;
}

const RevivedLeadsPanel: React.FC<RevivedLeadsPanelProps> = ({
  revived,
  claimsInRange,
  rangeLabel,
  loading = false,
  error = null,
}) => {
  const r = revived;
  const empty = !r || r.revivals === 0;
  const callsTaken = r ? r.shows + r.no_shows : 0;
  const close = r ? creditedCloseCounts(r) : null;

  return (
    <div className="liquid-glass anim-lift p-4 space-y-5" data-testid="revived-leads-panel">
      <div>
        <h3 className="token-14s mb-3 flex items-center gap-2">
          <span className="section-bar" />
          Revived Lost Leads
          <span className="token-10 text-glass-muted font-normal normal-case tracking-normal ml-1">
            claimed off triage · won back{rangeLabel ? ` · ${rangeLabel}` : ""}
          </span>
        </h3>

        {loading ? (
          <div
            className="rounded-2xl border border-black/[0.06] bg-black/[0.02] px-4 py-6 text-center"
            data-testid="revived-leads-loading"
          >
            <div className="token-13m text-glass">Loading claims…</div>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-ds-red/20 bg-ds-red/10 px-4 py-6 text-center">
            <div className="token-13m text-ds-red">Could not load revived leads</div>
            <div className="token-11 text-glass-muted mt-1">{error}</div>
          </div>
        ) : empty ? (
          <div
            className="rounded-2xl border border-black/[0.06] bg-black/[0.02] px-4 py-6 text-center"
            data-testid="revived-leads-empty"
          >
            <div className="token-13m text-glass">No revived leads from claims in this range</div>
            <div className="token-11 text-glass-muted mt-1">
              {claimsInRange > 0
                ? `${formatNumber(claimsInRange)} claims made, none dialed and booked yet.`
                : "No claims were made in this range."}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <MetricCard label="Revived Cash" value={formatUSD(r.revival_cash)} hero />
              <MetricCard label="Deals Closed" value={r.revival_wins.toString()} />
              <MetricCard
                label="Close Rate"
                value={pctOrDash(close?.wins ?? 0, close?.decisive ?? 0)}
                sub={`pending ${pctOrDash(close?.withPending ?? 0, close?.decisive ?? 0)}`}
              />
              <MetricCard
                label="Show-up Rate"
                value={pctOrDash(r.shows, showRateDenominator(r))}
                sub={`${r.no_shows} no-show${r.no_shows === 1 ? "" : "s"} · ${r.orphan_precalls} orphan`}
              />
            </div>

            <div className="grid grid-cols-3 gap-x-6 gap-y-4 mt-4">
              <div>
                <div className="token-10u text-glass-muted">Calls Taken</div>
                <div className="token-15 text-glass">{callsTaken.toLocaleString("en-US")}</div>
              </div>
              <div>
                <div className="token-10u text-glass-muted">Revived Claims</div>
                <div className="token-15 text-glass">{r.revivals.toLocaleString("en-US")}</div>
              </div>
              <div>
                <div className="token-10u text-glass-muted">Avg Cash / Close</div>
                <div className="token-15 text-glass">
                  {r.revival_wins ? formatUSD(r.revival_cash / r.revival_wins) : "—"}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Overlap disclosure: a revived lead the closer marked "No Setter" is self-set too. */}
      {!empty && r.selfset_rows > 0 && (
        <div
          className="rounded-2xl border border-black/[0.06] bg-black/[0.02] px-3 py-2"
          data-testid="revived-overlap-note"
        >
          <div className="token-11 text-glass">
            {r.selfset_rows} call{r.selfset_rows === 1 ? "" : "s"} counted on both cards
          </div>
          <div className="token-10l text-glass-muted mt-0.5">
            {r.selfset_wins} close{r.selfset_wins === 1 ? "" : "s"} · {formatUSD(r.selfset_cash)}. A
            revived lead the closer marked &ldquo;No Setter&rdquo; is self-set too. The two cards
            overlap by design; they do not sum.
          </div>
        </div>
      )}

      {/* Receipt: the predicate on the face of the card + the drill-down. */}
      <div className="token-9 text-glass-muted pt-1 flex items-center justify-between gap-2">
        <span>
          n={r?.revivals ?? 0} revived claim{r?.revivals === 1 ? "" : "s"} · booked while the claim
          was live · the claimer closed or set it
        </span>
        <Link
          to="/admin/triage?view=analytics"
          className="text-ds-accent hover:underline whitespace-nowrap"
          data-testid="revived-leads-drilldown"
        >
          Who closed what →
        </Link>
      </div>
    </div>
  );
};

export default RevivedLeadsPanel;
